// Plan P9.16: approval dashboard. Every planned post (video + caption + link + slot) is reviewed here
// before the Pixel posts it; the gate can be switched off per channel once that channel is trusted.
import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

interface Plan {
  key: string; platform: 'tiktok' | 'facebook'; account: string; productName: string; shopProductId?: string;
  videoName: string; videoRelPath: string; plannedAt: string; caption: string; firstComment?: string; aiGenerated: boolean;
  status: string; approvedBy?: string; supersedes?: string; receipt?: { permalink?: string; note?: string };
  keyword: { display: string; secondary: string[] };
}
type Gate = { tiktok: boolean; facebook: boolean };

const STATUS_COLOR: Record<string, string> = {
  pending_approval: 'var(--studio-saffron)', approved: 'var(--studio-gold)', queued_on_device: 'var(--studio-terracotta)',
  published: '#2e7d32', needs_human: 'var(--studio-chili)', failed: 'var(--studio-chili)', superseded: '#888', rejected: '#888',
};
const ictTime = (iso: string) => new Date(iso).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', hour12: false });

export default function PublishingApprovalPanel() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [gate, setGate] = useState<Gate>({ tiktok: true, facebook: true });
  const [device, setDevice] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/affiliate-harness/publishing/plans');
      setPlans(r.data.plans); setGate(r.data.approvalGate); setError('');
    } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); }
    try { setDevice((await api.get('/affiliate-harness/publishing/device-state')).data); } catch { setDevice(null); }
  }, []);
  useEffect(() => { void load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, [load]);

  const act = async (key: string, action: 'approve' | 'reject' | 'execute' | 'sync') => {
    setBusy(key + action);
    try {
      const body = action === 'reject' ? { note: window.prompt('Lý do từ chối?') ?? '' } : {};
      await api.post(`/affiliate-harness/publishing/plans/${key}/${action}`, body); await load();
    } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); } finally { setBusy(''); }
  };
  const toggleGate = async (platform: keyof Gate) => {
    if (gate[platform] && !window.confirm(`Tắt duyệt tay cho ${platform}? Bài sẽ tự đăng theo lịch.`)) return;
    try { setGate((await api.put('/affiliate-harness/publishing/approval-gate', { [platform]: !gate[platform] })).data); } catch (e: any) { setError(String(e)); }
  };
  const kill = async () => {
    if (!window.confirm('Dừng ngay toàn bộ thao tác trên điện thoại?')) return;
    try { await api.post('/affiliate-harness/publishing/kill', { reason: 'Studio kill switch' }); await load(); } catch (e: any) { setError(e?.response?.data?.error ?? String(e)); }
  };

  const open = plans.filter(p => showClosed || !['superseded', 'rejected'].includes(p.status));
  const breaker = (device?.breaker as { tripped?: boolean; reason?: string } | undefined);
  return (
    <div style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Duyệt đăng bài</h2>
        {(['tiktok', 'facebook'] as const).map(p => (
          <label key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={gate[p]} onChange={() => toggleGate(p)} /> Duyệt tay {p}
          </label>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          Thiết bị: {device ? (device.killed ? '⛔ đã dừng' : breaker?.tripped ? `⚠️ breaker: ${breaker.reason}` : '✅ sẵn sàng') : 'không kết nối'}
        </span>
        <button onClick={kill} style={{ background: 'var(--studio-chili)', color: '#fff', border: 0, padding: '6px 12px', borderRadius: 6 }}>Kill switch</button>
        <label><input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> hiện bài đã thay/huỷ</label>
      </div>
      {error && <div style={{ color: 'var(--studio-chili)' }}>{error}</div>}
      {!open.length && <div>Chưa có kế hoạch đăng nào.</div>}
      {open.map(p => (
        <div key={p.key} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr', gap: 12 }}>
          <video src={`/api/affiliate-harness/publishing/plans/${p.key}/video`} controls style={{ width: '100%', borderRadius: 6, background: '#000' }} />
          <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <strong>{p.platform === 'tiktok' ? 'TikTok' : 'Facebook'} · {p.account}</strong>
              <span style={{ color: STATUS_COLOR[p.status] ?? '#555' }}>● {p.status}</span>
              <span>🕒 {ictTime(p.plannedAt)} ICT</span>
              {p.aiGenerated && <span>🤖 nhãn AI</span>}
              {p.supersedes && <span>↻ lịch mới</span>}
            </div>
            <div>{p.productName}{p.shopProductId ? ` · SKU ${p.shopProductId}` : ''} · từ khoá: <em>{p.keyword.display}</em></div>
            <pre style={{ whiteSpace: 'pre-wrap', margin: 0, background: '#faf7f2', padding: 8, borderRadius: 6 }}>{p.caption}</pre>
            {p.firstComment && <div>Bình luận ghim: <code>{p.firstComment}</code></div>}
            {p.receipt?.permalink && <a href={p.receipt.permalink} target="_blank" rel="noreferrer">{p.receipt.permalink}</a>}
            {p.receipt?.note && p.status !== 'published' && <div style={{ color: 'var(--studio-chili)' }}>{p.receipt.note}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              {p.status === 'pending_approval' && <button disabled={!!busy} onClick={() => act(p.key, 'approve')}>Duyệt</button>}
              {['pending_approval', 'approved'].includes(p.status) && <button disabled={!!busy} onClick={() => act(p.key, 'reject')}>Từ chối</button>}
              {p.status === 'approved' && <button disabled={!!busy} onClick={() => act(p.key, 'execute')}>Gửi sang điện thoại</button>}
              {p.status === 'queued_on_device' && <button disabled={!!busy} onClick={() => act(p.key, 'sync')}>Cập nhật trạng thái</button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
